import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { parseISO, differenceInDays } from 'date-fns';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Plus, 
  Ship, 
  Loader2,
  Target,
  Clock,
  Briefcase,
  PoundSterling,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';

// Import extracted components
import { TripCard, TripDialog, UnlinkedTripsBanner } from '../components/trips';
import MarkReturnedDialog from '../components/trips/MarkReturnedDialog';
import { AttachDocumentDialog } from './documents';
import { PreviewDialog } from './monthly-earnings/DocumentDialogs';
import { useTaxYear } from '../context/TaxYearContext';
import { useSubscription } from '../context/SubscriptionContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Trips = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightTripId = searchParams.get('highlight');
  const tripRefs = useRef({});
  
  const [trips, setTrips] = useState([]);
  const { selectedYear } = useTaxYear();
  const { canAddContent } = useSubscription();
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTrip, setEditingTrip] = useState(null);
  const [expandedTrip, setExpandedTrip] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tripToDelete, setTripToDelete] = useState(null);
  const [highlightedTrip, setHighlightedTrip] = useState(highlightTripId);
  const [stats, setStats] = useState(null);
  const [tripDocs, setTripDocs] = useState({}); // { tripId: [docs] }
  const [attachDialogOpen, setAttachDialogOpen] = useState(false);
  const [attachingTrip, setAttachingTrip] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [markReturnedTrip, setMarkReturnedTrip] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const url = selectedYear && selectedYear !== 'all'
        ? `${API_URL}/api/dashboard/stats?tax_year=${selectedYear}`
        : `${API_URL}/api/dashboard/stats`;
      const response = await axios.get(url);
      setStats(response.data);
    } catch (err) {
      // silent
    }
  }, [selectedYear]);

  const fetchTrips = useCallback(async () => {
    setLoading(true);
    try {
      const url = selectedYear && selectedYear !== 'all'
        ? `${API_URL}/api/trips?tax_year=${selectedYear}`
        : `${API_URL}/api/trips`;
      const response = await axios.get(url);
      setTrips(response.data.trips || []);
    } catch (err) {
      // Swallow aborted/cancelled requests (Safari aborts on background/foreground transitions).
      // Also swallow 401 — ProtectedRoute redirects to login on auth loss.
      if (axios.isCancel(err) || err?.code === 'ERR_CANCELED' || err?.response?.status === 401) return;
      // Real error — clear stale data so we don't show an inconsistent list,
      // and surface a toast so the user knows something went wrong (rather than
      // silently seeing zero trips).
      setTrips([]);
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      toast.error(
        status === 500
          ? 'Server error loading trips. Try selecting a specific tax year, then contact support.'
          : detail || 'Failed to load trips'
      );
    } finally {
      setLoading(false);
    }
  }, [selectedYear]);

  useEffect(() => {
    fetchTrips();
    fetchStats();
  }, [fetchTrips, fetchStats]);

  // Fetch linked documents for all trips
  const fetchTripDocs = useCallback(async (tripIds) => {
    try {
      const results = {};
      await Promise.all(
        tripIds.map(async (tid) => {
          const res = await axios.get(`${API_URL}/api/documents/trip/${tid}`);
          results[tid] = res.data.documents || [];
        })
      );
      setTripDocs(prev => ({ ...prev, ...results }));
    } catch (err) {
      // silent
    }
  }, []);

  // Fetch docs when trips load or expanded trip changes
  useEffect(() => {
    if (expandedTrip) {
      fetchTripDocs([expandedTrip]);
    }
  }, [expandedTrip, fetchTripDocs]);

  const handleAttachDoc = (trip) => {
    setAttachingTrip(trip);
    setAttachDialogOpen(true);
  };

  const handleUnlinkDoc = async (doc) => {
    try {
      await axios.put(`${API_URL}/api/documents/${doc.id}/link`, { trip_id: null });
      toast.success('Document unlinked');
      if (expandedTrip) fetchTripDocs([expandedTrip]);
    } catch (err) {
      toast.error('Failed to unlink document');
    }
  };

  // Handle highlight parameter - scroll to and highlight the trip from the calendar
  useEffect(() => {
    if (highlightTripId && trips.length > 0) {
      // Find the trip and expand it
      const tripToHighlight = trips.find(t => t.id === highlightTripId);
      if (tripToHighlight) {
        setExpandedTrip(highlightTripId);
        setHighlightedTrip(highlightTripId);
        
        // Scroll to the trip after a short delay to allow DOM to update
        setTimeout(() => {
          const element = tripRefs.current[highlightTripId];
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
        
        // Clear the highlight after 3 seconds
        setTimeout(() => {
          setHighlightedTrip(null);
          // Clear the URL parameter
          setSearchParams({});
        }, 3000);
      }
    }
  }, [highlightTripId, trips, setSearchParams]);

  const handleDelete = async (tripId, tripName) => {
    setTripToDelete({ id: tripId, name: tripName });
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!tripToDelete) return;
    
    try {
      await axios.delete(`${API_URL}/api/trips/${tripToDelete.id}`);
      toast.success('Trip deleted');
      fetchTrips();
    } catch (error) {
      toast.error('Failed to delete trip');
    } finally {
      setDeleteDialogOpen(false);
      setTripToDelete(null);
    }
  };

  const handleEdit = (trip) => {
    setEditingTrip(trip);
    setDialogOpen(true);
  };

  const handleSuccess = (savedTripId) => {
    const tripId = savedTripId || editingTrip?.id;
    setDialogOpen(false);
    setEditingTrip(null);
    setExpandedTrip(null);
    fetchTrips().then(() => {
      fetchStats();
      if (tripId) {
        setTimeout(() => {
          const el = document.querySelector(`[data-testid="trip-card-${tripId}"]`);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
    });
  };

  // Calculate trip statistics from the trips array
  const tripStats = useMemo(() => {
    if (!trips || trips.length === 0) return null;
    
    // Count trips by type
    const workTrips = trips.filter(t => t.trip_type === 'work_trip').length;
    const holidays = trips.filter(t => t.trip_type === 'qualifying_holiday').length;
    const hotelStays = trips.filter(t => t.trip_type === 'hotel_stay' || t.trip_type === 'uk_hotel_stay').length;
    const courses = trips.filter(t => t.trip_type === 'course_uk' || t.trip_type === 'course_foreign').length;
    const paidLeave = trips.filter(t => t.trip_type === 'paid_leave').length;
    
    // Calculate total days on trips (using vessel dates for work trips, or UK dates)
    let totalTripDays = 0;
    trips.forEach(trip => {
      const startDate = trip.date_joined_vessel || trip.date_left_uk;
      const endDate = trip.date_left_vessel || trip.date_returned_uk;
      if (startDate && endDate) {
        try {
          const days = differenceInDays(parseISO(endDate), parseISO(startDate)) + 1;
          if (days > 0) totalTripDays += days;
        } catch (e) {
          // silent
        }
      }
    });
    
    // Calculate average trip duration
    const avgDuration = totalTripDays > 0 ? Math.round(totalTripDays / trips.length) : 0;
    
    // Find longest trip
    let longestTrip = 0;
    let longestTripName = '';
    trips.forEach(trip => {
      const startDate = trip.date_joined_vessel || trip.date_left_uk;
      const endDate = trip.date_left_vessel || trip.date_returned_uk;
      if (startDate && endDate) {
        try {
          const days = differenceInDays(parseISO(endDate), parseISO(startDate)) + 1;
          if (days > longestTrip) {
            longestTrip = days;
            longestTripName = trip.vessel_name || trip.hotel_name || trip.course_name || 'Trip';
          }
        } catch (e) {
          // silent
        }
      }
    });
    
    return {
      workTrips,
      holidays,
      hotelStays,
      courses,
      paidLeave,
      totalTripDays,
      avgDuration,
      longestTrip,
      longestTripName
    };
  }, [trips]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="trips-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Trips</h1>
          <p className="text-slate-400 mt-1">Manage your work trips and qualifying holidays</p>
        </div>
        
        <div className="flex items-center gap-3">
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) setEditingTrip(null);
          }}>
            <DialogTrigger asChild>
              <Button className="bg-amber-600 hover:bg-amber-700 disabled:opacity-50" data-testid="add-trip-btn" disabled={!canAddContent}>
                <Plus className="h-4 w-4 mr-2" />
                Add Trip
              </Button>
            </DialogTrigger>
          <TripDialog 
            trip={editingTrip} 
            onSuccess={handleSuccess} 
            onClose={() => {
              setDialogOpen(false);
              setEditingTrip(null);
            }}
            existingTrips={trips}
          />
        </Dialog>
        </div>
      </div>

      {/* Summary Cards */}
      {trips.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          {/* Total Trips */}
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-blue-500/20 rounded-lg">
                  <Briefcase className="h-4 w-4 sm:h-5 sm:w-5 text-blue-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] sm:text-xs text-slate-500 uppercase tracking-wide">Total Trips</p>
                  <p className="text-xl sm:text-2xl font-bold text-white">{trips.length}</p>
                </div>
              </div>
              {tripStats && (
                <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-slate-700 text-[10px] sm:text-xs text-slate-500">
                  <span className="text-blue-400">{tripStats.workTrips}</span> work • 
                  <span className="text-purple-400 ml-1">{tripStats.holidays}</span> holiday
                  {tripStats.courses > 0 && <span className="hidden sm:inline"> • <span className="text-teal-400">{tripStats.courses}</span> course</span>}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Qualifying Days */}
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-amber-500/20 rounded-lg">
                  <Target className="h-4 w-4 sm:h-5 sm:w-5 text-amber-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] sm:text-xs text-slate-500 uppercase tracking-wide">Qualifying Days</p>
                  <p className="text-xl sm:text-2xl font-bold text-amber-400">
                    {stats?.qualifying_days?.toFixed(0) || trips.reduce((sum, t) => sum + (t.qualifying_days || 0), 0)}
                  </p>
                </div>
              </div>
              <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-slate-700">
                <div className="flex justify-between text-[10px] sm:text-xs">
                  <span className="text-slate-500">Qualifying days this year</span>
                  <span className="text-amber-400">
                    {stats?.qualifying_days?.toFixed(0) || 0} days
                  </span>
                </div>
                <div className="mt-1 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full rounded-full transition-all bg-amber-500"
                    style={{ width: `${Math.min(100, ((stats?.qualifying_days || 0) / 183) * 100)}%` }}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Average Duration */}
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-purple-500/20 rounded-lg">
                  <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-purple-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] sm:text-xs text-slate-500 uppercase tracking-wide">Avg Duration</p>
                  <p className="text-xl sm:text-2xl font-bold text-purple-400">
                    {tripStats?.avgDuration || 0} <span className="text-xs sm:text-sm font-normal text-slate-500">days</span>
                  </p>
                </div>
              </div>
              {tripStats && tripStats.longestTrip > 0 && (
                <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-slate-700 text-[10px] sm:text-xs text-slate-500 truncate">
                  Longest: <span className="text-purple-400">{tripStats.longestTrip}</span> days
                  <span className="hidden sm:inline"> ({tripStats.longestTripName})</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* SED Status */}
          <Card className={`col-span-2 sm:col-span-1 border ${stats?.sed_eligible ? 'bg-amber-500/10 border-amber-500/30' : 'bg-slate-800 border-slate-700'}`}>
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className={`p-1.5 sm:p-2 rounded-lg ${stats?.sed_eligible ? 'bg-amber-500/20' : 'bg-slate-700'}`}>
                  {stats?.sed_eligible ? (
                    <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 text-amber-400" />
                  ) : (
                    <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 text-slate-400" />
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] sm:text-xs text-slate-500 uppercase tracking-wide">SED Status</p>
                  <p className={`text-base sm:text-lg font-bold ${stats?.sed_eligible ? 'text-amber-400' : 'text-slate-400'}`}>
                    {stats?.sed_eligible ? 'Eligible' : 'In Progress'}
                  </p>
                </div>
              </div>
              <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-slate-700/50 text-[10px] sm:text-xs text-slate-500">
                {stats?.claim_broken ? (
                  <span className="text-red-400">Claim broken — exceeded 183 consecutive UK days</span>
                ) : stats?.sed_eligible && stats?.eligible_period ? (
                  <div>
                    <span className="text-amber-400">
                      {stats.eligible_period.ongoing ? 'Ongoing period' : 'Valid period'} since {new Date(stats.eligible_period.start).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}
                    </span>
                    <span className="text-slate-500 ml-1">({stats.eligible_period.total_days?.toLocaleString()} days)</span>
                  </div>
                ) : stats?.sed_eligible ? (
                  <span className="text-amber-400">Eligible for SED</span>
                ) : (
                  <span>Days in hand: <span className="text-cyan-400">{stats?.days_in_hand?.toFixed(0) || 0}</span></span>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Unlinked Trips Banner */}
      {trips.length > 0 && (
        <UnlinkedTripsBanner trips={trips} onEdit={handleEdit} />
      )}

      {/* Trips List */}
      {trips.length === 0 ? (
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="py-12 text-center">
            <Ship className="h-12 w-12 text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-300 mb-2">No trips yet</h3>
            <p className="text-slate-500 mb-4">Add your first work trip or qualifying holiday to start tracking.</p>
            <Button 
              onClick={() => setDialogOpen(true)}
              className="bg-amber-600 hover:bg-amber-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Your First Trip
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {trips.map((trip) => (
            <div
              key={trip.id}
              ref={(el) => { tripRefs.current[trip.id] = el; }}
              className={`transition-all duration-500 ${highlightedTrip === trip.id ? 'ring-2 ring-amber-500 ring-offset-2 ring-offset-slate-900 rounded-lg' : ''}`}
            >
              <TripCard 
                trip={trip} 
                expanded={expandedTrip === trip.id}
                onToggle={() => setExpandedTrip(expandedTrip === trip.id ? null : trip.id)}
                onEdit={() => handleEdit(trip)}
                onDelete={() => handleDelete(trip.id, trip.vessel_name || trip.hotel_name || trip.trip_type)}
                onMarkReturned={(t) => setMarkReturnedTrip(t)}
                allTrips={trips}
                linkedDocs={tripDocs[trip.id] || []}
                onAttachDoc={handleAttachDoc}
                onPreviewDoc={(doc) => setPreviewDoc(doc)}
                onUnlinkDoc={handleUnlinkDoc}
              />
            </div>
          ))}
        </div>
      )}

      {/* Attach Document to Trip Dialog */}
      <Dialog open={attachDialogOpen} onOpenChange={(open) => {
        setAttachDialogOpen(open);
        if (!open) setAttachingTrip(null);
      }}>
        {attachingTrip && (
          <AttachDocumentDialog
            trip={attachingTrip}
            onSuccess={() => {
              setAttachDialogOpen(false);
              setAttachingTrip(null);
              if (expandedTrip) fetchTripDocs([expandedTrip]);
            }}
            onClose={() => { setAttachDialogOpen(false); setAttachingTrip(null); }}
          />
        )}
      </Dialog>

      {/* Document Preview Dialog */}
      <PreviewDialog
        previewDoc={previewDoc}
        onClose={() => setPreviewDoc(null)}
        onDownload={(id, name) => {
          axios.get(`${API_URL}/api/documents/${id}/preview`, { responseType: 'blob' })
            .then(res => {
              const url = URL.createObjectURL(res.data);
              const a = document.createElement('a');
              a.href = url;
              a.download = name || 'document';
              a.click();
              URL.revokeObjectURL(url);
            });
        }}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-slate-800 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Trip</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Are you sure you want to delete {tripToDelete?.name ? `"${tripToDelete.name}"` : 'this trip'}? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-700 text-white border-slate-600 hover:bg-slate-600">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Mark as Returned quick action */}
      <MarkReturnedDialog
        trip={markReturnedTrip}
        open={!!markReturnedTrip}
        onOpenChange={(o) => { if (!o) setMarkReturnedTrip(null); }}
        onCompleted={() => {
          setMarkReturnedTrip(null);
          fetchTrips();
          fetchStats();
        }}
      />
    </div>
  );
};


export default Trips;
